// Claymation Ad's voice and storyboard stages (live's `voice-stage`, `storyboard-stage`,
// `shot-plan-strip`, `shot-spec-summary` and `clay-still-card`; spec G14-claymation-ad, PR d2; not
// captured, built from live's code). The voice: the narrator's takes, a card each, live's pick and
// "Add a take". The storyboard: the shot plan, the stills in render order, ↻ on each, Retry the
// shot director after it failed, Back to voice, and the shot spec folded under the stills.
import { ActionIcon, Button, Group, Stack, Text, Tooltip } from "@mantine/core";
import { ArrowLeft, Plus, RotateCcw } from "lucide-react";
import { useState } from "react";

import type {
  ClaymationDraftStill,
  ClaymationScript,
  ClaymationShotPlan,
  ClaymationShotSpec,
  ClaymationStillName,
  ClaymationTake,
} from "../../../api/generated/model";
import { MediaPreview, type PreviewItem } from "../../../platform/media/MediaPreview";
import { FieldLabel } from "../../../ui/FieldLabel";
import { plannedVoiceSeconds, scriptWords } from "./claymation-script";
import { StillCard } from "./StillCard";
import { TakeCard } from "./TakeCard";
import claymation from "./claymation.module.css";
import classes from "./podcast.module.css";

/** Live's words (`library-view/constants.ts`). */
const VOICE_TOOLTIP =
  "Three takes of the narration, each checked word for word against the script; the best one is " +
  "picked for you. Listen to the last word — it must fall. The picked take's timing sets every " +
  "shot and every caption.";
const STORYBOARD_TOOLTIP =
  "The clay character sheet, the clay product sheet, the empty sets and the first frame of every " +
  "shot, drawn before any video, and the end card. ↻ redraws an image and every image built on it.";
const ADD_TAKE = "Add a take";
const UNPLANNABLE_TAKE = "A line was not heard in this take, so its shots cannot be timed.";
const MAX_TAKES = 6;
const BACK_TO_VOICE = "Back to voice";
const RETRY_SHOT_DIRECTOR = "Retry the shot director";
const SPEC_LABELS = { camera: "camera", detail: "detail", turn: "turn" } as const;

export interface VoiceStageProps {
  script: ClaymationScript;
  takes: readonly ClaymationTake[];
  note: string | null;
  /** The take picked: the member's, else the one live pre-picks. */
  chosen: number | null;
  busy: boolean;
  onPick: (take: number) => void;
  onAddTake: () => void;
}

/** Live's voice stage: "Voice" with its info, "planned voice ≈ {P} s" and "+" (off at six takes
 * or while one is made), a card a take with its last word and pace, then live's note. */
export function VoiceStage({ script, takes, note, chosen, busy, onPick, onAddTake }: VoiceStageProps) {
  const making = takes.some((take) => take.status === "working" || take.status === "pending");
  return (
    <Stack gap={12} component="section" aria-label="Voice">
      <div className={classes.scriptHeader}>
        <Text component="div" className={classes.scriptTitle}>
          <FieldLabel label="Voice" info={VOICE_TOOLTIP} />
        </Text>
        <Group gap={8} wrap="nowrap">
          <Text className={classes.meta}>
            planned voice ≈ {Math.round(plannedVoiceSeconds(scriptWords(script)))} s
          </Text>
          <Tooltip label={ADD_TAKE} withArrow>
            <span>
              <ActionIcon
                variant="subtle"
                aria-label={ADD_TAKE}
                disabled={takes.length >= MAX_TAKES || making || busy}
                onClick={onAddTake}
              >
                <Plus size={16} aria-hidden />
              </ActionIcon>
            </span>
          </Tooltip>
        </Group>
      </div>
      <Stack gap={8}>
        {takes.map((take) => (
          <TakeCard
            key={take.number}
            take={take}
            picked={chosen === take.number}
            busy={busy}
            unpickable={UNPLANNABLE_TAKE}
            details={
              take.vo_end === null || take.pace_wps === null
                ? null
                : `last word ${take.vo_end} s · ${take.pace_wps} w/s`
            }
            onPick={() => onPick(take.number)}
          />
        ))}
      </Stack>
      {note === null ? null : <Text className={claymation.note}>{note}</Text>}
    </Stack>
  );
}

/** A still's label (live's `toStillLabel`). */
export function stillLabel(name: ClaymationStillName, script: ClaymationScript): string {
  if (name === "end_card") return "End card";
  if (name === "sheet_character") return `${script.character.name} sheet`;
  if (name === "product_sheet") return "Product sheet";
  if (name.startsWith("plate_")) return `Set ${name.slice("plate_".length).toUpperCase()}`;
  const shot = name.slice("shot_".length).replace("_turn", "");
  return name.endsWith("_turn") ? `Shot ${shot} · turned` : `Shot ${shot}`;
}

/** Live's shot plan strip: each shot's seconds and lines, a drift over a second marked, the end
 * card's seconds. */
function PlanStrip({ plan }: { plan: ClaymationShotPlan }) {
  const card = plan.end_card;
  return (
    <div className={claymation.strip}>
      {plan.shots.map((shot) => {
        const drift = Math.abs(shot.drift_s) > 1 ? [`⚠ ${shot.drift_s > 0 ? "+" : ""}${shot.drift_s} s`] : [];
        const lines = `line ${shot.lines.map((line) => line.line_id).join(", ")}`;
        return (
          <div key={shot.shot_id} className={claymation.segment}>
            <Text className={claymation.segmentTitle}>Shot {shot.shot_id}</Text>
            <Text className={claymation.segmentLabel}>
              {[`${shot.start_s}–${shot.start_s + shot.length_s} s`, lines, ...drift].join(" · ")}
            </Text>
          </div>
        );
      })}
      <div className={claymation.segment}>
        <Text className={claymation.segmentTitle}>End card</Text>
        <Text className={claymation.segmentLabel}>
          {card.start_s}–{card.start_s + card.length_s} s
        </Text>
      </div>
    </div>
  );
}

/** Live's shot spec summary, folded: the character, the palette, the sets, the product, the shots. */
function SpecSummary({ spec }: { spec: ClaymationShotSpec }) {
  const blocks = [
    { title: "Character", lines: [spec.character.lock] },
    { title: "Palette", lines: [spec.palette] },
    { title: "Sets", lines: spec.sets.map((set) => `${set.id} ${set.place} · ${set.world_lock}`) },
    {
      title: "Product",
      lines: [
        `${spec.product.pack_type} · ${spec.product.size_line}`,
        ...spec.product.items.map((item) => `${item.name} · ${item.height_cm} cm · ${item.look}`),
      ],
    },
    {
      title: "Shots",
      lines: spec.shots.map((shot) =>
        [
          `Shot ${shot.shot_id}`,
          shot.scene,
          `${SPEC_LABELS.camera}: ${shot.camera_angle}`,
          `${SPEC_LABELS.detail}: ${shot.whimsical_detail}`,
          ...(shot.orientation_change ? [`${SPEC_LABELS.turn}: ${shot.orientation_change}`] : []),
        ].join(" · "),
      ),
    },
  ];
  return (
    <details className={claymation.spec}>
      <summary className={claymation.specTitle}>Shot spec</summary>
      <Stack gap="xs">
        {blocks.map((block) => (
          <Stack key={block.title} gap={0}>
            <Text className={claymation.segmentTitle}>{block.title}</Text>
            {block.lines.map((line, at) => (
              <Text key={at} className={claymation.summaryLine}>
                {line}
              </Text>
            ))}
          </Stack>
        ))}
      </Stack>
    </details>
  );
}

export interface StoryboardStageProps {
  script: ClaymationScript;
  spec: ClaymationShotSpec | null;
  plan: ClaymationShotPlan | null;
  stills: readonly ClaymationDraftStill[];
  specWarning: string | null;
  shotFailed: boolean;
  /** The shot director or a still works: a still not yet drawn shows live's loader. */
  drawing: boolean;
  busy: boolean;
  /** An ask is on its way: Back to voice waits for it (the stills draw on, as live's). */
  asking: boolean;
  onRegenerate: (name: ClaymationStillName) => void;
  onRetry: () => void;
  onBack: () => void;
}

/** Live's storyboard stage: "Storyboard" with its info, the plan's lengths and Back to voice; the
 * plan strip, live's warning on the spec, Retry the shot director, the still cards in render
 * order (the sheets wide), each previewed in the shared lightbox, and the shot spec. */
export function StoryboardStage(props: StoryboardStageProps) {
  const { script, spec, plan, stills, specWarning, shotFailed, drawing, busy, asking } = props;
  const [preview, setPreview] = useState<string | null>(null);
  const made: PreviewItem[] = stills.flatMap((still) =>
    still.url === null
      ? []
      : [
          {
            id: still.name,
            name: stillLabel(still.name, script),
            kind: "image" as const,
            url: still.url,
            thumb: still.url,
            poster: null,
            durationMs: null,
            width: null,
            height: null,
          },
        ],
  );
  return (
    <Stack gap={12} component="section" aria-label="Storyboard">
      <div className={classes.scriptHeader}>
        <Text component="div" className={classes.scriptTitle}>
          <FieldLabel label="Storyboard" info={STORYBOARD_TOOLTIP} />
        </Text>
        <Group gap={8} wrap="nowrap">
          {plan === null ? null : (
            <Text className={classes.meta}>
              video {plan.video_seconds} s · voice ends {plan.vo_end} s
            </Text>
          )}
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<ArrowLeft size={14} aria-hidden />}
            disabled={asking}
            onClick={props.onBack}
          >
            {BACK_TO_VOICE}
          </Button>
        </Group>
      </div>
      {plan === null ? null : <PlanStrip plan={plan} />}
      {specWarning === null ? null : <Text className={classes.warning}>⚠ {specWarning}</Text>}
      {shotFailed ? (
        <Group>
          <Button
            variant="light"
            size="compact-sm"
            leftSection={<RotateCcw size={14} aria-hidden />}
            disabled={busy}
            onClick={props.onRetry}
          >
            {RETRY_SHOT_DIRECTOR}
          </Button>
        </Group>
      ) : null}
      <div className={classes.stills}>
        {stills.map((still) => (
          <StillCard
            key={still.name}
            label={stillLabel(still.name, script)}
            still={still}
            waiting={drawing}
            busy={busy}
            wide={still.name === "sheet_character" || still.name === "product_sheet"}
            onRegenerate={() => props.onRegenerate(still.name)}
            onFixLabel={null}
            onPreview={() => setPreview(still.name)}
          />
        ))}
      </div>
      {spec === null ? null : <SpecSummary spec={spec} />}
      <MediaPreview items={made} current={preview} onShow={setPreview} />
    </Stack>
  );
}
